import React from "react";
import { useMutation, useApolloClient } from "@apollo/client";
import { Form, Input, Button, ConfigProvider, message } from "antd";
import { USER_LOGIN } from "graphql/mutation/user-gql";
import { IS_LOGGED_IN } from "graphql/query/local-store-gql";
import { GET_ME } from "graphql/query/user-gql";
import { errorNotification } from "components/request-result";
import Localize, { localizeByKey, localizeByKeyStatic } from "components/service/localize";
import customizeTheme from "components/customize-theme";

import logo from "assets/logo.svg";

interface ILoginFormValues
{
    username: string;
    password: string;
}

const SignInForm: React.FC = (): JSX.Element =>
{
    const [ form ] = Form.useForm();
    const client = useApolloClient();

    const [ loginRequest, { loading } ] = useMutation(USER_LOGIN, {
        update(cache, { data: { userLogin } })
        {
            const { access_token } = userLogin;

            localStorage.setItem("authToken", access_token);
            cache.writeQuery({
                query: IS_LOGGED_IN,
                data: {
                    isLoggedIn: access_token,
                },
            });

            // Check user permissions after login
            checkUserPermissions();
        },
        onError(error)
        {
            errorNotification(error);
        },
    });

    // Function to check if user has login permission
    const checkUserPermissions = async () => {
        try {
            // Refetch user data to get permissions
            const { data: meData } = await client.query({
                query: GET_ME,
                fetchPolicy: 'network-only'
            });

            if (meData?.me?.roles) {
                const isDeveloper = meData.me.roles.some((role: any) => role.slug === 'role_developer');

                if (!isDeveloper) {
                    // Check if user has library login permission
                    const hasLoginPermission = meData.me.roles.some((role: any) =>
                        role.permissions?.some((perm: any) =>
                            perm.entry_point === 'library' && perm.slug === 'permission_library_login'
                        )
                    );

                    if (!hasLoginPermission) {
                        // User doesn't have access to system
                        message.error(localizeByKeyStatic('PERMISSION.Error_NoAccessToSystem'));
                        // Clear auth token and stay on login page
                        localStorage.removeItem('authToken');
                        client.cache.writeQuery({
                            query: IS_LOGGED_IN,
                            data: {
                                isLoggedIn: false,
                            },
                        });
                        return;
                    }
                }
            }
        } catch (error) {
            console.error('Error checking user permissions:', error);
        }
    };

    return (
        <div className="sign-in-form">
            <div className="holder-date-logo">
                <div className="logo">
                    <div className="logo-box">
                        <img src={logo} alt="My Stand Project" />
                    </div>
                </div>
            </div>

            <strong style={{ color: "var(--primary-color)" }}>
                <Localize>FORMS.Input_Label_SignIn</Localize>
            </strong>

            <ConfigProvider theme={customizeTheme.LightTheme()}>
                <Form
                    className=""
                    form={form}
                    layout="vertical"
                    onFinish={(values: ILoginFormValues) =>
                    {
                        loginRequest({
                            variables: {
                                data: {
                                    username: values.username,
                                    password: values.password,
                                },
                            },
                        }).catch((error) =>
                        {
                            errorNotification(error);
                        });
                    }}
                >
                    <Form.Item
                        label={<Localize>FORMS.Input_Label_Login</Localize>}
                        name="username"
                    >
                        <Input
                            name="username"
                            placeholder={
                                localizeByKey("FORMS.Input_Placeholder_Email")
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        label={<Localize>FORMS.Input_Label_Password</Localize>}
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: <Localize>FORM_RULES.Required_Password</Localize>,
                            },
                        ]}
                    >
                        <Input.Password
                            name="password"
                            placeholder={
                                localizeByKey("FORMS.Input_Placeholder_Password")
                            }
                        />
                    </Form.Item>
                    <Form.Item>
                        <div className="form-actions">
                            <Button type="primary" htmlType="submit" loading={loading}>
                                <Localize>GLOBAL.Button_Text_Login</Localize>
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </ConfigProvider>
        </div>
    );
};

export default SignInForm;
